/**
 * Copy-workflow review-grid constants — the per-platform column set, editable
 * set, editor map and list-field capabilities CopyPlanStep hands to CLW's
 * shared ReviewGrid + CellDetailPanel.
 *
 * EDITABLE BY DEFAULT, exactly like clwGridConstants.js: every column of the
 * grid a family renders is editable unless that family's DENYLIST names it,
 * and every entry of a denylist carries the reason there is no write target
 * the copy LAUNCH would consume. (This module used to hand-maintain four
 * allow-lists; the rationale it gave — "CLW writes campaign-scoped fields we
 * can't" — inverted once CLW's own appliers started writing parent entities
 * through the same shared writers. What actually differs now is the TEMPLATE
 * SHAPE, which is what the denylists below encode.)
 *
 * What still makes the copy sets NARROWER than CLW's, per family:
 *
 *   - DV360. The copy path launches through dv360_campaign_validator.py,
 *     whose per-level key ALLOWLISTS are hard errors ("Unknown lineItem
 *     field: 'x'"), and through dv360_campaign_preprocessor.py's li_min,
 *     which drops anything it doesn't name. Every DV360 field left editable
 *     here writes a key on BOTH lists (or an IO/campaign-level key on the IO
 *     allowlist: displayName / entityStatus / pacing / kpi / budget /
 *     optimizationObjective / lineItems / frequencyCap / insertionOrderType).
 *     Separately, a DV360 copy carries NO targeting in the template — it
 *     rides launcherResult.targeting_plan, applied post-create — so every
 *     targeting cell is REMOVE-ONLY through the plan and denylisted for the
 *     scalar editor (see copyReviewEdits.js's targeting section).
 *   - TTD. The copy template is copy_ttd_campaign_launcher's curated shape:
 *     the ad group states AdGroupName / IsEnabled / BaseBidCPM / MaxBidCPM /
 *     ROIGoal / FunnelLocation / ChannelId / NewFrequencyConfigs /
 *     AssociatedBidLists / ThirdPartyAudienceId, and the campaign states a
 *     CURRENCY Budget + DailyBudget, dates, PacingMode. Everything MMGY-only
 *     (geo lists, MmgyDeals, MmgyAudiences, DeviceType, AdEnvironment,
 *     impression budgets) is absent by construction: the copy carries the
 *     source's geo/deal/device targeting BY REFERENCE in AssociatedBidLists.
 *   - Google Ads. The copy templates ARE the shape gads/gadsRowBuilders +
 *     gads/gadsEdits were written against, so the sets derive from CLW's own
 *     denylist plus the few copy-grid specifics (the sticky column is the
 *     CAMPAIGN, match types are per-keyword, PMax media/targeting keys the
 *     copy template spells differently).
 *
 * CAMPAIGN-SCOPED fields (dates, budgets, campaign name, TTD pacing, Google
 * Ads bid targets) are editable FROM CHILD ROWS on purpose — the value being
 * edited is the COPY's own flight/budget/name, shared by every sibling row by
 * definition. CLW's grids do the same thing now (see gadsEdits.js's SCOPE
 * note and reviewEdits.js's parent-campaign writers).
 *
 * EVERY field left editable has a write-back applier in copyReviewEdits.js —
 * the same "never show a saved state that didn't persist" rule
 * clwGridConstants.js documents, enforced field-by-field by the round-trip
 * tests in copyReviewEdits.test.js.
 */
import {
  GROUP_COLS,
  editableFieldsFor,
  DV360_FIELD_EDITORS,
  TTD_FIELD_EDITORS,
} from '../CampaignLaunchWorkflow/clwGridConstants';
import {
  GADS_FIELD_EDITORS,
  gadsGroupCols,
  gadsListFields,
} from '../CampaignLaunchWorkflow/gads/gadsConstants';

// ── Columns the COPY grid drops everywhere ───────────────────────────
// A copy grid column that can never differ between the source and the copy is
// horizontal scrolling the user pays for and learns nothing from.
//
// `flightType` is exactly that on every platform: the copy builders pin the
// child's flight to the campaign's, so the cell is the literal "Inherits
// campaign" (DV360 pins li.flight to *_INHERITED; TTD and Google Ads have no
// child-level flight at all). Every platform's copy denylist already names it
// read-only — this takes the next step and stops rendering it. Start/End in
// the same group still show the flight itself.
const COPY_HIDDEN_COLS = new Set(['flightType']);

// ── DSP (DV360 / TTD) column set for the COPY flow ───────────────────
// CLW's columns minus the ones a copy cannot use.
//
// The whole `creative` group goes: a DSP copy launches as a shell, so Asset,
// Sizes, Click URL and Tracker are all static or derived there (every one of
// the four is on both COPY_DV360_DENYLIST and COPY_TTD_DENYLIST below). On
// TTD nothing creative comes across at all; on DV360 the source's creatives
// are re-associated at launch, which the tab could only restate as a count.
// Google Ads keeps ITS creative group — those columns are headlines,
// descriptions and final URLs, which a copy really does carry and edit.
const COPY_HIDDEN_DSP_GROUPS = new Set(['creative']);

export function copyDspGroupCols() {
  const out = {};
  for (const [group, cols] of Object.entries(GROUP_COLS)) {
    if (COPY_HIDDEN_DSP_GROUPS.has(group)) continue;
    out[group] = (cols || []).filter((c) => !COPY_HIDDEN_COLS.has(c.k));
  }
  return out;
}
export const copyDspGroupKeys = () => Object.keys(copyDspGroupCols());

// ── Google Ads column set for the COPY flow ──────────────────────────
// The headline number a copy is judged by is the DAILY budget typed in step 1
// — CLW's Overview shows a total instead, which (once its row builder divided
// that total across the campaign's ad groups) turned one input into several
// different numbers on screen. Swap Overview's Budget column for the Daily
// budget the campaign was actually built with; the Budget tab still carries
// Daily AND Total, so nothing is hidden.
//
// Lives here (not in CopyPlanStep) because the editable sets below DERIVE from
// it: a column added to the copy grid becomes editable the moment it exists,
// unless a denylist names it.
export function copyGadsGroupCols(subtype) {
  const base = gadsGroupCols(subtype);
  const adGroupLabel = subtype === 'search' ? 'Ad group' : 'Asset group';
  const out = {};
  for (const [group, cols] of Object.entries(base)) {
    const mapped = (cols || [])
      // `market` IS the campaign name, which is now the grid's sticky first
      // column (overlayGadsCopyRows moves it onto `name`) — a second Campaign
      // column would just repeat it.
      .filter((c) => c.k !== 'market' && !COPY_HIDDEN_COLS.has(c.k))
      .map((c) => (group === 'overview' && c.k === 'budget'
        ? { ...c, k: 'dailyBudget', l: 'Daily budget' }
        : c));
    // Ad/asset group leads every group, directly after the sticky campaign.
    out[group] = [{ k: 'adGroupName', l: adGroupLabel, w: 220 }, ...mapped];
  }
  return out;
}

// ── DV360 — rows are line items (rowId = template lineItemId, which is the
//    SOURCE line item id carried through as the ref key). ──────────────────
export const COPY_DV360_DENYLIST = new Set([
  // ACTIVATION IS THE LAUNCH ACTION'S JOB, NOT A CELL — see the long note on
  // DV360_DENYLIST in ../CampaignLaunchWorkflow/clwGridConstants.js. The copy
  // grid needs this more than CLW's does, not less: its edits SAVE IMMEDIATELY
  // with no unsaved-changes bar and no undo, so a mis-typed Status cell is
  // committed the moment it is entered.
  'entityStatus',   // a review surface must not promote an entity to a live state
  // Derived / no storage of their own — same reasons as CLW's DV360 denylist.
  'fmt',            // derived from li.lineItemType / displayName
  'creative',       // asset COUNT, overlaid with the creative_plan re-association chip
  'impTracker',     // static 'DV360 native' label
  'bidCeiling',     // TTD-only column — DV360 rows render '—'
  'flightType',     // the copy builder pins li.flight to *_INHERITED; LI custom flights are stripped at launch
  // Package-derived cells. The copy template carries NO packages[] (the
  // validator's config allowlist has no slot for them) and its IO displayName
  // is the SOURCE's, with none of MMGY's "Package_Format_Category ($7.5)"
  // encoding — so CLW's writers (packages[].rate, the "($7.5)" name suffix,
  // budgetAmountInImpressions, packages[].units) have nothing to write to.
  'package',        // the row reads io.packageKey — not an allowed IO key
  'category',       // parsed out of the IO displayName tail
  'creativeFormat', // packages[].placement_sizes
  'cpm',            // packages[].rate / the name-rate suffix — edit `bidFloor`, which is what the copy cell is overlaid from
  'imps',           // budgetSegments[].budgetAmountInImpressions / packages[].units — the copy IO states a CURRENCY segment
  'budget',         // derived (cpm × imps): with no package rate there is nothing to solve against
  // Shell launch: li.assets is [] and the copy template has no result-level
  // assets[], so CLW's clickURL writer (asset.clickUrl) has no target.
  'clickURL',
  // Targeting. A DV360 copy states no targeting keys at all — the source's
  // targeting rides launcherResult.targeting_plan and is applied post-create
  // via bulkEditAssignedTargetingOptions. Writing li.geoTargeting/audiences/…
  // would launch targeting TWICE (and for the singleton types the plan is
  // applied last and wins). These cells stay editable BY REMOVAL through the
  // plan — see copyReviewEdits.js and CopyPlanStep's removal panel.
  'geo',
  'audiences',
  'keywords',
  'interests',
  'pmp',
  'devices',
  'environment',
  'viewability',    // TARGETING_TYPE_VIEWABILITY is a plan SINGLETON (delete-then-create) — it overrides the template's viewability key at launch
  'brandSafety',    // content-label / sensitive-category exclusions, also plan-borne
]);
export const COPY_DV360_EDITABLE = editableFieldsFor(GROUP_COLS, COPY_DV360_DENYLIST);
// Editor types are CLW's — every DV360 copy column that takes a typed value is
// the same column, with the same units, as on the CLW grid.
export const COPY_DV360_FIELD_EDITORS = { ...DV360_FIELD_EDITORS };

// ── TTD — rows are ad groups (rowId = template AdGroupId = source id). ────
export const COPY_TTD_DENYLIST = new Set([
  // Activation is the launch action's job, not a cell (see COPY_DV360_DENYLIST
  // above). TTD is where this actually launched money: the copy launcher builds
  // every copied ad group with `IsEnabled: False` — "copy is created
  // paused/disabled" — but that is BUILD-time, and a grid edit is applied
  // afterwards, so IsEnabled rode straight into POST /adgroup with no
  // creator-side pause to catch it.
  'entityStatus',   // IsEnabled — the copy is built paused ON PURPOSE; the grid must not undo that
  // Same derived/no-storage entries CLW's TTD denylist carries.
  'fmt',            // derived from ChannelId / PrimaryChannel / name
  'package',        // the parent CampaignId identifier
  'creative',       // asset / creative_plan COUNT
  'flightType',     // hardcoded 'Inherits campaign' literal
  'impTracker',     // static 'TTD pixel' label
  'pixel',          // static 'None' literal
  'convWindow',     // '—' literal
  'viewability',    // '—' literal — the builder reads no ad group key for it
  'clickURL',       // creative-scoped on TTD
  'creativeFormat', // creative-scoped (sizes ride the creatives)
  'interests',      // always '—' on TTD
  'bidStrategy',    // derived READ-ONLY from Base/Max CPM
  'bidGoal',        // duplicates kpi + kpiTarget (same ROIGoal read)
  // COPY-SPECIFIC. The copy template is copy_ttd_campaign_launcher's curated
  // shape; the keys CLW's remaining TTD writers target are absent from it and
  // what they represent is carried BY REFERENCE instead.
  'tactic',         // no `tactic` key on a copy ad group — the row DERIVES it from the ad group NAME (deriveTtdTacticFromName); a written one is display-only and would ride the ad group POST as a stray field
  'geo',            // the copy's geo lives in the source's geo BID LISTS (AssociatedBidLists, passed by reference); the grid renders review_enrichment, a display-only sidecar with no write-back
  'pmp',            // same: deal ids come from the source bid lists' BidLines via review_enrichment — the copy carries no MmgyDeals
  'audiences',      // the copy carries ONE already-merged ThirdPartyAudienceId, not MMGY's ResolvedAudiences list CLW's writer prunes
  'keywords',       // MmgyAudiences.ThirdPartyAudiences is MMGY-only — a copy has no such list
  'expansion',      // MmgyAudiences.ExpandWithThirdPartyAudienceSearch — same, MMGY-only
  'devices',        // the copy's device targeting rides the source's bid lists; writing DeviceType would create a SECOND, conflicting device bid list at launch
  'environment',    // same, for AdEnvironment
  'brandSafety',    // MarketplaceOptOut isn't carried by the copy template
  'imps',           // parent.BudgetInImpressions — the copy states a CURRENCY Budget/DailyBudget; adding an impression budget gives the campaign two conflicting budget kinds
  'dailyCap',       // parent.DailyBudgetInImpressions — same conflict (the copy's daily cap is DailyBudget, in currency)
]);
export const COPY_TTD_EDITABLE = new Set([
  ...editableFieldsFor(GROUP_COLS, COPY_TTD_DENYLIST),
  // Not a grid COLUMN (GROUP_COLS has no `market` on TTD) but kept in the set:
  // the duplicate-name gate's quick-fix and the row dump address the campaign
  // name through this key, and applyTtdCopyFields writes it.
  'market',
]);
export const COPY_TTD_FIELD_EDITORS = { ...TTD_FIELD_EDITORS };

// ── Google Ads — Search rows are ad groups, PMax/Demand Gen rows are asset
//    groups; rowId is positional (`gads-{ci}-{ai}`). ────────────────────────
const COPY_GADS_SHARED_DENYLIST = [
  // Activation is the launch action's job, not a cell — one rule across all
  // three platforms (see COPY_DV360_DENYLIST above).
  'entityStatus',   // campaign.status — the copy creators build PAUSED on purpose
  'fmt',            // row type tag derived from the subtype (SEM / PMAX / DGEN)
  'flightType',     // hardcoded 'Inherits campaign' literal
  'channel',        // sticky 'Google Ads' label (not a grid column; listed for completeness)
  // MATCH TYPE is stored PER KEYWORD in the copy templates ({keyword,
  // match_type}, straight off the importer — see
  // copy_google_ads_campaign_launcher._map_keywords). overlayGadsCopyRows
  // renders the DISTINCT SET of an ad group's types; one ad-group-level write
  // has no meaning against per-keyword values and the creator ignores it.
  // Each keyword's own type round-trips through the keywords list instead.
  'matchType',
];
const COPY_GADS_ASSET_DENYLIST = [
  ...COPY_GADS_SHARED_DENYLIST,
  'assetKey',       // the copy grid overlays the asset group's real NAME here — edit it through the Asset group column, which writes asset_groups[].name
  'creativeFormat', // derived count summary ('3 headlines · 2 descriptions') — edit the copy lists instead
  // The copy PMax template states its campaign as name / dates / budget /
  // location_ids / final_url / asset_groups[] (+ tracking template) — there is
  // no campaign-level `targeting` block, because PMax audience signals ride
  // each ASSET GROUP's search_themes (the `keywords` cell, which is editable).
  'audiences',
  'age',
  'gender',
  'parental',
  'income',
  'languages',      // not carried by the PMax copy template (Search's is)
  'videos',         // the copy carries YouTube films as `youtube_video_ids`; the grid's Videos cell reads `videos`, which the copy template never states
];
export const COPY_GADS_DENYLIST_BY_SUBTYPE = {
  search: new Set(COPY_GADS_SHARED_DENYLIST),
  pmax: new Set(COPY_GADS_ASSET_DENYLIST),
  demandgen: new Set(COPY_GADS_ASSET_DENYLIST),
};

const copyGadsEditableFor = (subtype) => editableFieldsFor(
  copyGadsGroupCols(subtype),
  COPY_GADS_DENYLIST_BY_SUBTYPE[subtype] || COPY_GADS_DENYLIST_BY_SUBTYPE.search,
);

export const COPY_GADS_EDITABLE_BY_SUBTYPE = {
  // `market` is filtered out of the copy column set (the campaign moved to the
  // sticky `name` column) but stays in the editable set: it is the
  // duplicate-name gate's quick-fix key, and applyGadsCopyFields writes it.
  search: new Set([...copyGadsEditableFor('search'), 'market']),
  pmax: new Set([...copyGadsEditableFor('pmax'), 'market']),
  demandgen: new Set([...copyGadsEditableFor('demandgen'), 'market']),
};
// Kept as named exports — the two subtypes the copy launchers actually build.
export const COPY_GADS_SEARCH_EDITABLE = COPY_GADS_EDITABLE_BY_SUBTYPE.search;
export const COPY_GADS_PMAX_EDITABLE = COPY_GADS_EDITABLE_BY_SUBTYPE.pmax;

export const COPY_GADS_FIELD_EDITORS = { ...GADS_FIELD_EDITORS };

// ── List-shaped cells ────────────────────────────────────────────────
// Same contract as TTD_LIST_FIELDS / DV360_LIST_FIELDS / gadsListFields:
//   { [fieldKey]: { ops: { add, edit, remove }, addPlaceholder?, reason?, note? } }
// `reason` renders INSTEAD of the add input when add is off (it says why);
// `note` renders UNDER the add input when add is on. geo stages ops against
// the two sub-fields the panel uses everywhere — geoInclude / geoExclude.

// DV360 copies: targeting is the targeting_plan sidecar, applied verbatim
// post-create. Pruning entries from it is deterministic; ADDING targeting
// needs DV360 targetingOptionId lookups (a typed market name has no option id
// to create with), so every field here is REMOVE-ONLY and says so.
const DV360_COPY_REMOVE_ONLY = {
  ops: { add: false, edit: false, remove: true },
  reason: 'Adding DV360 targeting needs option-ID lookups — remove only for now',
};
// GEO is the exception: the copy launcher already resolves location NAMES to
// targetingOptionIds at launch (search_geo_region, service-side) through the
// per-line-item `geo_plan` the broad build-time override records. So a typed
// market name is enough here — the grid writes geo_plan and the launch resolves
// it, additively, leaving the row's existing regions untouched.
const DV360_COPY_GEO = {
  ops: { add: true, edit: true, remove: true },
  addPlaceholder: 'Add a market by name…',
  note: 'Typed names are resolved to DV360 regions at launch and added to this line item\'s existing geo',
};
export const COPY_DV360_LIST_FIELDS = {
  keywords: DV360_COPY_REMOVE_ONLY,
  audiences: DV360_COPY_REMOVE_ONLY,
  interests: DV360_COPY_REMOVE_ONLY,
  pmp: DV360_COPY_REMOVE_ONLY,
  geoInclude: DV360_COPY_GEO,
  geoExclude: DV360_COPY_GEO,
};

// TTD copies: geo, deals and audiences ride the SOURCE's bid lists, which the
// copy passes by reference (AssociatedBidLists) and never re-creates — the
// grid renders the review_enrichment sidecar, which has no write-back. So the
// lists are read-only and the panel says where they come from instead of
// offering ops the save could not honour.
// Geo is the exception: the TTD launcher accepts a per-ad-group `Geography`
// directive and builds the bid list itself, so the copy can OWN its geo instead
// of only referencing the source's (see applyTtdGeoOps).
//
// Adds are FREE-FORM — type a market name and the launcher resolves it to a
// GeoSegmentId at launch, the same contract DV360_COPY_GEO above already uses.
// This replaced a ttd-geo picker that TTD rate-limited into uselessness: four
// location-type queries per keystroke returned 429s, so nothing could be added
// at all.
const TTD_COPY_GEO = {
  ops: { add: true, edit: true, remove: true },
  addPlaceholder: 'Add location…',
  note: 'Locations resolve to TTD geo segments at launch; editing geo gives this copy its own bid list, leaving the source untouched',
};
const TTD_COPY_READ_ONLY = {
  ops: { add: false, edit: false, remove: false },
  reason: 'Carried from the source campaign’s bid lists by reference — edit the source, or the copy after launch',
};
export const COPY_TTD_LIST_FIELDS = {
  keywords: TTD_COPY_READ_ONLY,
  audiences: TTD_COPY_READ_ONLY,
  pmp: TTD_COPY_READ_ONLY,
  geoInclude: TTD_COPY_GEO,
  geoExclude: TTD_COPY_GEO,
};

// Google Ads copies: the templates state locations, keywords, search themes,
// audiences and copy as plain text with no pre-launch ids, so CLW's list
// configs apply as-is. Two copy-specific overrides below.
const COPY_GADS_LIST_FIELDS_BY_SUBTYPE = {
  search: gadsListFields('search'),
  pmax: {
    ...gadsListFields('pmax'),
    // Images: the copy carries each field type's URLs in its own plural list
    // (logo_images / marketing_images / square_marketing_images /
    // portrait_marketing_images) — which is what the creator uploads. An ADD
    // can only land in the generic `images` list, which the PMax creator does
    // not read, so it would show in the grid without launching.
    creative: {
      ops: { add: false, edit: true, remove: true },
      reason: 'Adding images needs a typed slot (logo / marketing / square) — replace or remove the existing ones here',
    },
    // PMax copies target by geo-target constant id (`location_ids`); the
    // creator ignores `locations` names (see
    // copy_pmax_campaign_launcher._build_campaign). Each id is shown under
    // Google's name for it when the copy resolved a typed override, and
    // removing that label removes the id (gadsEdits applyGeoDeletions).
    geoInclude: {
      ...gadsListFields('pmax').geoInclude,
      note: 'PMax copies target by geo-target ID — remove a location to stop targeting it; add one by its ID (a typed name is not targeted)',
    },
    // `audiences` and `videos` are denylisted for scalar editing above because
    // the copy PMax template carries NEITHER key (audience signals ride each
    // asset group's search_themes; films ride `youtube_video_ids`). They still
    // inherited CLW's list configs through the spread, so the panel offered add
    // boxes and × buttons that mutated `campaign.targeting.audience_segments`
    // and `asset.videos` — keys the PMax copy creator never reads. Those edits
    // saved to the template and then launched nothing, silently discarding the
    // user's intent. State them read-only so the panel explains itself.
    audiences: {
      ops: { add: false, edit: false, remove: false },
      reason: "PMax audience signals ride each asset group's search themes — edit the Keywords cell instead",
    },
    videos: {
      ops: { add: false, edit: false, remove: false },
      reason: 'The copy carries YouTube films as youtube_video_ids, which this cell does not read yet',
    },
  },
};
COPY_GADS_LIST_FIELDS_BY_SUBTYPE.demandgen = COPY_GADS_LIST_FIELDS_BY_SUBTYPE.pmax;

/**
 * Column set, editable set, editor map and list-field capabilities for a
 * build's review platform. `listFields` drives CellDetailPanel's per-item
 * add / edit / remove controls; `groupCols`/`groupKeys` are null for the
 * DV360/TTD families, which render CLW's shared GROUP_COLS unchanged.
 */
export function copyEditableFor(family, gadsSubtype) {
  if (family === 'ttd') {
    return {
      editableFields: COPY_TTD_EDITABLE,
      fieldEditors: COPY_TTD_FIELD_EDITORS,
      listFields: COPY_TTD_LIST_FIELDS,
    };
  }
  if (family === 'gads') {
    const subtype = gadsSubtype === 'search' ? 'search' : (gadsSubtype || 'pmax');
    return {
      editableFields: COPY_GADS_EDITABLE_BY_SUBTYPE[subtype] || COPY_GADS_EDITABLE_BY_SUBTYPE.pmax,
      fieldEditors: COPY_GADS_FIELD_EDITORS,
      listFields: COPY_GADS_LIST_FIELDS_BY_SUBTYPE[subtype] || COPY_GADS_LIST_FIELDS_BY_SUBTYPE.pmax,
    };
  }
  return {
    editableFields: COPY_DV360_EDITABLE,
    fieldEditors: COPY_DV360_FIELD_EDITORS,
    listFields: COPY_DV360_LIST_FIELDS,
  };
}

/** List-field capabilities alone, for hosts that don't need the editable set. */
export const copyListFieldsFor = (family, gadsSubtype) => copyEditableFor(family, gadsSubtype).listFields;
